import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  List,
  ListItem,
  Paper,
  Snackbar,
  Alert,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import * as signalR from '@microsoft/signalr';
import { useAuth } from '../../contexts/AuthContext';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { NETAPI_URL } from '../../config/api';
import { infiniteRetryPolicy, startResilientConnection } from '../../utils/signalRConnection';

// ============================================================================
// CANLI DESTEK PANELİ (SuperAdmin)
// ============================================================================
// SuperAdmin sayfası açıkken çalışır:
//  - SignalR /hubs/support → JoinAgents: yeni talepler anlık düşer.
//  - Yeni talepte SESLİ UYARI (paket siparişteki 3'lü bip deseni, Web Audio,
//    1.5 sn'de bir tekrar) — bekleyen talep kalmayınca durur. Sayfası açık olan
//    HERKES duyar; İLK devralan (adıyla) görüşmeyi üstlenir, diğerleri
//    "X devraldı" görür (backend atomik claim: geç kalan 409 alır).
//  - Devralan yetkiliye chat penceresi açılır; mesajlar hub'dan canlı akar.
//  - İZLEME MODU: ikinci buton (göz ikonu) tüm açık görüşmeleri listeler;
//    yönetici/diğer temsilciler herhangi bir görüşmeyi SALT OKUNUR açabilir,
//    mesajlar hub'ın SupportAgents yayını üzerinden canlı akar, müdahale edilemez.
//  - İleride otomatik atama eklenecek; şimdilik manuel devralma (kullanıcı isteği).
// ============================================================================

interface SupportSession {
  id: number;
  visitorName: string | null;
  status: 'waiting' | 'active' | 'closed';
  claimedByUserId: number | null;
  claimedByName: string | null;
  createdAt: string;
  lastMessageAt: string | null;
}

interface SupportMessage {
  id: number;
  sender: 'visitor' | 'agent' | 'system';
  senderName: string | null;
  content: string;
  createdAt: string;
}

/** API/SignalR alanlarını hem camelCase hem PascalCase okuyabilen yardımcı. */
const pick = (obj: any, key: string): any =>
  obj?.[key] ?? obj?.[key.charAt(0).toUpperCase() + key.slice(1)];

const mapSession = (raw: any): SupportSession => ({
  id: pick(raw, 'id'),
  visitorName: pick(raw, 'visitorName') ?? null,
  status: pick(raw, 'status') ?? 'waiting',
  claimedByUserId: pick(raw, 'claimedByUserId') ?? null,
  claimedByName: pick(raw, 'claimedByName') ?? null,
  createdAt: pick(raw, 'createdAt'),
  lastMessageAt: pick(raw, 'lastMessageAt') ?? null,
});

const mapMessage = (raw: any): SupportMessage => ({
  id: pick(raw, 'id'),
  sender: pick(raw, 'sender'),
  senderName: pick(raw, 'senderName') ?? null,
  content: String(pick(raw, 'content') ?? ''),
  createdAt: pick(raw, 'createdAt'),
});

const fmtTime = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
};

/** Own-JWT Bearer ile SupportChat API isteği (SuperAdminService deseni). */
const supportApi = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const token = await ownAuthSession.getAccessToken();
  const response = await fetch(`${NETAPI_URL}/api/SupportChat${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => null);
  return { ok: response.ok, status: response.status, data };
};

const LiveSupportPanel: React.FC = () => {
  const { userDetails, currentUser } = useAuth();
  const theme = useTheme();
  // Mobilde chat/izleme pencereleri tam ekran açılır (küçük ekranda diyalog sıkışıyor)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [sessions, setSessions] = useState<SupportSession[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [claimingId, setClaimingId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'info' | 'success' | 'warning' | 'error' } | null>(null);

  // Aktif chat penceresi (devralınan görüşme)
  const [chatSession, setChatSession] = useState<SupportSession | null>(null);
  const [chatMessages, setChatMessages] = useState<SupportMessage[]>([]);
  const [chatText, setChatText] = useState('');
  const [chatSending, setChatSending] = useState(false);
  // Görüşme (özellikle müşteri tarafından) sonlandırıldıysa pencere açık kalır
  // ama yazma alanı kilitlenir ve içeride sistem mesajı gösterilir.
  const [chatClosed, setChatClosed] = useState(false);

  // İzleme modu (salt okunur): tüm açık görüşmelerin listesi + seçilen görüşmenin
  // canlı mesaj akışı. Müdahale yok — yalnızca okuma.
  const [monitorListOpen, setMonitorListOpen] = useState(false);
  const [monitorSession, setMonitorSession] = useState<SupportSession | null>(null);
  const [monitorMessages, setMonitorMessages] = useState<SupportMessage[]>([]);
  const monitorSessionIdRef = useRef<number | null>(null);
  const monitorListElRef = useRef<HTMLDivElement | null>(null);

  const hubRef = useRef<signalR.HubConnection | null>(null);
  const chatSessionIdRef = useRef<number | null>(null);
  const chatListRef = useRef<HTMLDivElement | null>(null);

  // Kendi devraldığımız görüşmelerin id'leri ("Görüşmeye Dön" butonu ayrımı için)
  const myClaimedIdsRef = useRef<Set<number>>(new Set());

  // ---- Sesli uyarı (paket siparişteki 3'lü bip deseni) ----
  const soundIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const stopAlertSound = useCallback(() => {
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch {
        // zaten kapalı olabilir
      }
      audioContextRef.current = null;
    }
  }, []);

  const startAlertSound = useCallback(() => {
    if (soundIntervalRef.current) return; // zaten çalıyor
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;

      const playAlertPattern = () => {
        const ctx = audioContextRef.current;
        if (!ctx) return;
        const now = ctx.currentTime;
        const beeps = [
          { freq: 1200, start: 0, duration: 0.12 },
          { freq: 800, start: 0.15, duration: 0.12 },
          { freq: 1400, start: 0.3, duration: 0.15 },
        ];
        beeps.forEach(({ freq, start, duration }) => {
          const oscillator = ctx.createOscillator();
          const gainNode = ctx.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(ctx.destination);
          oscillator.frequency.value = freq;
          oscillator.type = 'square';
          gainNode.gain.setValueAtTime(0, now + start);
          gainNode.gain.linearRampToValueAtTime(0.25, now + start + 0.02);
          gainNode.gain.setValueAtTime(0.25, now + start + duration - 0.02);
          gainNode.gain.linearRampToValueAtTime(0, now + start + duration);
          oscillator.start(now + start);
          oscillator.stop(now + start + duration);
        });
      };

      playAlertPattern();
      soundIntervalRef.current = setInterval(playAlertPattern, 1500);
    } catch (error) {
      console.error('📢 [LiveSupport] Ses çalınamadı:', error);
    }
  }, []);

  // Bekleyen talep varken ses çalar; kalmayınca (devralındı/kapandı) durur.
  const waitingCount = sessions.filter((s) => s.status === 'waiting').length;
  useEffect(() => {
    if (waitingCount > 0) startAlertSound();
    else stopAlertSound();
  }, [waitingCount, startAlertSound, stopAlertSound]);

  // İsim alanı: ad-soyad ile önceden dolu (düzenlenebilir)
  useEffect(() => {
    if (!agentName) {
      const name =
        [userDetails?.firstName, userDetails?.lastName].filter(Boolean).join(' ').trim() ||
        currentUser?.displayName ||
        currentUser?.email ||
        '';
      if (name) setAgentName(name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, currentUser]);

  const fetchSessions = useCallback(async () => {
    try {
      const { ok, data } = await supportApi('/sessions');
      if (ok) {
        const list = (pick(data, 'data') ?? []).map(mapSession);
        setSessions(list);
      }
    } catch {
      // sessiz: panel açıldığında tekrar denenir
    }
  }, []);

  // ---- SignalR ----
  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${NETAPI_URL}/hubs/support`, {
        // Prod IIS/proxy WebSocket upgrade'i ve SSE'yi geçirmiyor (handshake
        // canceled + "No Connection with that ID" 404). LongPolling doğrulandı;
        // SSE atlanır: WS başarısız olursa doğrudan LongPolling'e düşülür.
        // Sunucuda WebSocket Protocol etkinleştirilirse WS otomatik kullanılır.
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect(infiniteRetryPolicy)
      .configureLogging(signalR.LogLevel.None)
      .build();

    connection.on('SupportRequestCreated', (data: any) => {
      const session: SupportSession = {
        id: pick(data, 'sessionId'),
        visitorName: pick(data, 'visitorName') ?? null,
        status: 'waiting',
        claimedByUserId: null,
        claimedByName: null,
        createdAt: pick(data, 'createdAt'),
        lastMessageAt: null,
      };
      setSessions((prev) => (prev.some((s) => s.id === session.id) ? prev : [...prev, session]));
      setPanelOpen(true); // uyarı: panel kendiliğinden açılır
    });

    connection.on('SessionClaimed', (data: any) => {
      const sessionId = pick(data, 'sessionId');
      const claimedByName = pick(data, 'claimedByName');
      const claimedByUserId = pick(data, 'claimedByUserId');
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId ? { ...s, status: 'active' as const, claimedByName, claimedByUserId } : s
        )
      );
      // İzleme penceresi açıksa başlıktaki devralan bilgisini güncelle
      if (monitorSessionIdRef.current === sessionId) {
        setMonitorSession((prev) =>
          prev ? { ...prev, status: 'active', claimedByName, claimedByUserId } : prev
        );
      }
    });

    connection.on('SessionClosed', (data: any) => {
      const sessionId = pick(data, 'sessionId');
      const closedBy = pick(data, 'closedBy');
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      myClaimedIdsRef.current.delete(sessionId);
      if (chatSessionIdRef.current === sessionId) {
        // Açık chat penceresi: kilitle + içeride sistem mesajı göster
        setChatClosed(true);
        setChatMessages((prev) => [
          ...prev,
          {
            id: -Date.now(), // yerel bildirim (sunucu mesajlarıyla çakışmaz)
            sender: 'system' as const,
            senderName: null,
            content:
              closedBy === 'visitor' ? 'Müşteri görüşmeyi sonlandırdı.' : 'Görüşme sonlandırıldı.',
            createdAt: new Date().toISOString(),
          },
        ]);
      }
      // İzleme penceresi: kapanışı sistem notu olarak göster (pencere açık kalır)
      if (monitorSessionIdRef.current === sessionId) {
        setMonitorSession((prev) => (prev ? { ...prev, status: 'closed' } : prev));
        setMonitorMessages((prev) => [
          ...prev,
          {
            id: -Date.now(),
            sender: 'system' as const,
            senderName: null,
            content:
              closedBy === 'visitor' ? 'Müşteri görüşmeyi sonlandırdı.' : 'Görüşme sonlandırıldı.',
            createdAt: new Date().toISOString(),
          },
        ]);
      }
    });

    // Mesajlar SupportAgents grubuna da yayınlanır — açık chat penceresine ekle,
    // listede son mesaj zamanını güncelle. (Kendi mesajlarımız da hub ekosuyla
    // gelir; optimistik ekleme yapılmadığı için id bazlı dedupe yeterli.)
    connection.on('NewMessage', (data: any) => {
      const sessionId = pick(data, 'sessionId');
      const message = mapMessage(data);
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, lastMessageAt: message.createdAt } : s))
      );
      if (chatSessionIdRef.current === sessionId) {
        setChatMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      }
      if (monitorSessionIdRef.current === sessionId) {
        setMonitorMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      }
    });

    hubRef.current = connection;

    // Gruba katılma onConnected içinde: yeniden bağlanma yeni bir connection id
    // ürettiği için sunucudaki grup üyeliği kaybolur.
    let isFirstConnect = true;
    const stopConnection = startResilientConnection(connection, {
      label: 'LiveSupport',
      onConnected: async () => {
        await connection.invoke('JoinAgents');

        if (isFirstConnect) {
          isFirstConnect = false;
          return;
        }

        // Kopma süresince gelen talepleri yakala.
        fetchSessions();
      },
    });

    fetchSessions();

    return () => {
      stopAlertSound();
      stopConnection();
      hubRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    chatListRef.current?.scrollTo({ top: chatListRef.current.scrollHeight, behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    monitorListElRef.current?.scrollTo({ top: monitorListElRef.current.scrollHeight, behavior: 'smooth' });
  }, [monitorMessages]);

  // ---- Devralma ----
  const handleClaim = async (session: SupportSession) => {
    const name = agentName.trim();
    if (!name) {
      setSnackbar({ message: 'Lütfen önce adınızı yazın.', severity: 'warning' });
      return;
    }
    setClaimingId(session.id);
    try {
      const { ok, status, data } = await supportApi('/claim', {
        method: 'POST',
        body: JSON.stringify({ SessionId: session.id, AgentName: name }),
      });
      if (ok) {
        myClaimedIdsRef.current.add(session.id);
        // Yerel listeyi hemen güncelle: hub yayını gecikse/kopsa bile
        // bekleyen sayacı düşer ve sesli uyarı durur.
        setSessions((prev) =>
          prev.map((s) => (s.id === session.id ? { ...s, status: 'active' as const, claimedByName: name } : s))
        );
        openChat({ ...session, status: 'active', claimedByName: name });
      } else if (status === 409) {
        const byName = pick(data, 'claimedByName');
        setSnackbar({
          message: byName ? `Bu görüşmeyi ${byName} devraldı.` : 'Bu görüşme artık beklemede değil.',
          severity: 'warning',
        });
        fetchSessions();
      } else {
        setSnackbar({ message: 'Devralma başarısız oldu.', severity: 'error' });
      }
    } catch {
      setSnackbar({ message: 'Devralma başarısız oldu.', severity: 'error' });
    } finally {
      setClaimingId(null);
    }
  };

  // ---- Chat penceresi ----
  const openChat = async (session: SupportSession) => {
    chatSessionIdRef.current = session.id;
    setChatSession(session);
    setChatMessages([]);
    setChatClosed(false);
    try {
      const { ok, data } = await supportApi(`/session/${session.id}/messages`);
      if (ok) setChatMessages((pick(data, 'data') ?? []).map(mapMessage));
    } catch {
      // hub'dan gelen yeni mesajlar yine de görünür
    }
  };

  const closeChatWindow = () => {
    chatSessionIdRef.current = null;
    setChatSession(null);
    setChatMessages([]);
    setChatText('');
    setChatClosed(false);
  };

  const handleSendChat = async () => {
    const trimmed = chatText.trim();
    if (!trimmed || chatSending || !chatSession || chatClosed) return;
    setChatSending(true);
    try {
      const { ok, data } = await supportApi('/agent-message', {
        method: 'POST',
        body: JSON.stringify({ SessionId: chatSession.id, Content: trimmed }),
      });
      if (ok) {
        setChatText('');
      } else if (pick(data, 'code') === 'SESSION_CLOSED') {
        setSnackbar({ message: 'Görüşme kapanmış.', severity: 'info' });
        closeChatWindow();
        fetchSessions();
      } else if (pick(data, 'code') === 'NOT_OWNER') {
        setSnackbar({ message: `Bu görüşme ${pick(data, 'claimedByName') ?? 'başka bir yetkili'} üzerinde.`, severity: 'warning' });
      } else {
        setSnackbar({ message: 'Mesaj gönderilemedi.', severity: 'error' });
      }
    } catch {
      setSnackbar({ message: 'Mesaj gönderilemedi.', severity: 'error' });
    } finally {
      setChatSending(false);
    }
  };

  const handleCloseSession = async () => {
    if (!chatSession) return;
    try {
      await supportApi('/agent-close', {
        method: 'POST',
        body: JSON.stringify({ SessionId: chatSession.id }),
      });
    } catch {
      // SessionClosed yayını gelmese de pencereyi kapatıyoruz
    }
    closeChatWindow();
    fetchSessions();
  };

  // ---- İzleme modu (salt okunur) ----
  const openMonitor = async (session: SupportSession) => {
    monitorSessionIdRef.current = session.id;
    setMonitorSession(session);
    setMonitorMessages([]);
    try {
      const { ok, data } = await supportApi(`/session/${session.id}/messages`);
      if (ok) setMonitorMessages((pick(data, 'data') ?? []).map(mapMessage));
    } catch {
      // hub'dan gelen yeni mesajlar yine de görünür
    }
  };

  const closeMonitor = () => {
    monitorSessionIdRef.current = null;
    setMonitorSession(null);
    setMonitorMessages([]);
  };

  const activeMine = sessions.filter(
    (s) => s.status === 'active' && myClaimedIdsRef.current.has(s.id)
  );

  return (
    <>
      {/* Sağ alt köşe: canlı destek butonu (bekleyen sayısı rozetli) */}
      <Tooltip title="Canlı Destek Talepleri" placement="left" arrow>
        <Fab
          color={waitingCount > 0 ? 'error' : 'primary'}
          onClick={() => setPanelOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
            ...(waitingCount > 0 && {
              animation: 'liveSupportPulse 1s infinite',
              '@keyframes liveSupportPulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.12)' },
                '100%': { transform: 'scale(1)' },
              },
            }),
          }}
        >
          <Badge badgeContent={waitingCount} color="warning" overlap="circular">
            <SupportAgentIcon />
          </Badge>
        </Fab>
      </Tooltip>

      {/* İzleme butonu: tüm açık görüşmeler (salt okunur) */}
      <Tooltip title="Aktif Görüşmeler (İzleme)" placement="left" arrow>
        <Fab
          size="medium"
          onClick={() => {
            setMonitorListOpen(true);
            fetchSessions();
          }}
          sx={{
            position: 'fixed',
            bottom: 92,
            right: 28,
            zIndex: 1300,
            bgcolor: 'grey.700',
            color: 'white',
            '&:hover': { bgcolor: 'grey.800' },
          }}
        >
          <Badge badgeContent={sessions.filter((s) => s.status === 'active').length} color="success" overlap="circular">
            <VisibilityIcon />
          </Badge>
        </Fab>
      </Tooltip>

      {/* Talep listesi paneli */}
      <Dialog open={panelOpen} onClose={() => setPanelOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupportAgentIcon color="primary" />
          <Box sx={{ flex: 1 }}>Canlı Destek Talepleri</Box>
          <IconButton size="small" onClick={() => setPanelOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            label="Adınız (ziyaretçiye görünecek)"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value.slice(0, 100))}
            InputProps={{ startAdornment: <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            sx={{ mb: 2, mt: 0.5 }}
          />

          {sessions.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              Şu anda bekleyen veya aktif destek talebi yok.
            </Typography>
          )}

          <List disablePadding>
            {sessions.map((s) => {
              const mine = myClaimedIdsRef.current.has(s.id);
              return (
                <ListItem
                  key={s.id}
                  disableGutters
                  sx={{
                    border: '1px solid',
                    borderColor: s.status === 'waiting' ? 'warning.main' : 'divider',
                    borderRadius: 2,
                    mb: 1,
                    px: 1.5,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: s.status === 'waiting' ? 'warning.50' : 'background.paper',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      Talep #{s.id}
                      {s.visitorName ? ` — ${s.visitorName}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtTime(s.createdAt)}
                      {s.lastMessageAt ? ` · son mesaj ${fmtTime(s.lastMessageAt)}` : ''}
                    </Typography>
                  </Box>
                  {s.status === 'waiting' ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleClaim(s)}
                      disabled={claimingId === s.id}
                      startIcon={claimingId === s.id ? <CircularProgress size={14} color="inherit" /> : undefined}
                      sx={{ textTransform: 'none', flexShrink: 0 }}
                    >
                      Devral
                    </Button>
                  ) : mine ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openChat(s)}
                      sx={{ textTransform: 'none', flexShrink: 0 }}
                    >
                      Görüşmeye Dön
                    </Button>
                  ) : (
                    <Chip
                      size="small"
                      color="info"
                      label={`${s.claimedByName ?? 'Bir yetkili'} devraldı`}
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={fetchSessions} sx={{ textTransform: 'none' }}>
            Yenile
          </Button>
        </DialogActions>
      </Dialog>

      {/* İzleme listesi: tüm açık görüşmeler (bekleyen + aktif) */}
      <Dialog open={monitorListOpen} onClose={() => setMonitorListOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VisibilityIcon color="action" />
          <Box sx={{ flex: 1 }}>Aktif Görüşmeler (İzleme)</Box>
          <IconButton size="small" onClick={() => setMonitorListOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Tüm açık görüşmeler listelenir. Görüşmeler salt okunur izlenir; müdahale edilemez.
          </Typography>

          {sessions.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              Şu anda açık görüşme yok.
            </Typography>
          )}

          <List disablePadding>
            {sessions.map((s) => (
              <ListItem
                key={s.id}
                disableGutters
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 1,
                  px: 1.5,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    Talep #{s.id}
                    {s.visitorName ? ` — ${s.visitorName}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtTime(s.createdAt)}
                    {s.lastMessageAt ? ` · son mesaj ${fmtTime(s.lastMessageAt)}` : ''}
                  </Typography>
                </Box>
                {s.status === 'waiting' ? (
                  <Chip size="small" color="warning" label="Bekliyor" sx={{ flexShrink: 0 }} />
                ) : (
                  <Chip
                    size="small"
                    color="success"
                    icon={<PersonIcon />}
                    label={s.claimedByName ?? 'Yetkili'}
                    sx={{ flexShrink: 0 }}
                  />
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => openMonitor(s)}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  İzle
                </Button>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={fetchSessions} sx={{ textTransform: 'none' }}>
            Yenile
          </Button>
        </DialogActions>
      </Dialog>

      {/* İzleme penceresi: seçilen görüşme, SALT OKUNUR canlı akış */}
      <Dialog open={monitorSession !== null} onClose={closeMonitor} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <VisibilityIcon color="action" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              Talep #{monitorSession?.id}
              {monitorSession?.visitorName ? ` — ${monitorSession.visitorName}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {monitorSession?.status === 'waiting'
                ? 'Bekliyor — henüz devralınmadı'
                : monitorSession?.status === 'closed'
                  ? 'Görüşme sonlandı'
                  : `Temsilci: ${monitorSession?.claimedByName ?? '-'}`}
              {' · Salt okunur izleme'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeMonitor}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box
            ref={monitorListElRef}
            sx={{ height: { xs: 'calc(100dvh - 170px)', sm: 380 }, overflowY: 'auto', p: 1.5, bgcolor: 'grey.50' }}
          >
            {monitorMessages.map((m) =>
              m.sender === 'system' ? (
                <Box key={m.id} sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', bgcolor: 'grey.200', px: 1.5, py: 0.5, borderRadius: 4 }}
                  >
                    {m.content}
                  </Typography>
                </Box>
              ) : (
                <Box
                  key={m.id}
                  sx={{ display: 'flex', justifyContent: m.sender === 'agent' ? 'flex-end' : 'flex-start', mb: 1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      maxWidth: '80%',
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: m.sender === 'agent' ? 'success.main' : 'white',
                      color: m.sender === 'agent' ? 'white' : 'text.primary',
                      boxShadow: m.sender === 'agent' ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', opacity: 0.8 }}>
                      {m.sender === 'agent' ? m.senderName ?? 'Yetkili' : m.senderName ?? 'Ziyaretçi'} · {fmtTime(m.createdAt)}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.content}
                    </Typography>
                  </Paper>
                </Box>
              )
            )}
          </Box>
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', bgcolor: 'grey.100', gap: 1, alignItems: 'center' }}>
            <VisibilityIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Salt okunur izleme — bu görüşmeye mesaj gönderilemez.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Chat penceresi (devralınan görüşme) */}
      <Dialog open={chatSession !== null} onClose={closeChatWindow} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <SupportAgentIcon color="success" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              Talep #{chatSession?.id}
              {chatSession?.visitorName ? ` — ${chatSession.visitorName}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Yetkili: {chatSession?.claimedByName}
            </Typography>
          </Box>
          {!chatClosed && (
            <Button size="small" color="error" variant="outlined" onClick={handleCloseSession} sx={{ textTransform: 'none' }}>
              Görüşmeyi Kapat
            </Button>
          )}
          <IconButton size="small" onClick={closeChatWindow}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box
            ref={chatListRef}
            sx={{ height: { xs: 'calc(100dvh - 200px)', sm: 380 }, overflowY: 'auto', p: 1.5, bgcolor: 'grey.50' }}
          >
            {chatMessages.map((m) =>
              m.sender === 'system' ? (
                <Box key={m.id} sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', bgcolor: 'grey.200', px: 1.5, py: 0.5, borderRadius: 4 }}
                  >
                    {m.content}
                  </Typography>
                </Box>
              ) : (
                <Box
                  key={m.id}
                  sx={{ display: 'flex', justifyContent: m.sender === 'agent' ? 'flex-end' : 'flex-start', mb: 1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      maxWidth: '80%',
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: m.sender === 'agent' ? 'success.main' : 'white',
                      color: m.sender === 'agent' ? 'white' : 'text.primary',
                      boxShadow: m.sender === 'agent' ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', opacity: 0.8 }}>
                      {m.sender === 'agent' ? m.senderName ?? 'Yetkili' : m.senderName ?? 'Ziyaretçi'} · {fmtTime(m.createdAt)}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.content}
                    </Typography>
                  </Paper>
                </Box>
              )
            )}
          </Box>
          {chatClosed ? (
            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', bgcolor: 'grey.100' }}>
              <Typography variant="body2" color="text.secondary">
                Bu görüşme sonlandırıldı — mesaj gönderilemez.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={3}
                placeholder="Mesajınızı yazın..."
                value={chatText}
                onChange={(e) => setChatText(e.target.value.slice(0, 2000))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendChat();
                  }
                }}
              />
              <IconButton color="primary" onClick={handleSendChat} disabled={chatSending || !chatText.trim()}>
                {chatSending ? <CircularProgress size={20} /> : <SendIcon />}
              </IconButton>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Aktif görüşmesi olup pencereyi kapatmış yetkiliye hatırlatma çipi */}
      {!chatSession && activeMine.length > 0 && (
        <Chip
          icon={<SupportAgentIcon />}
          label={`${activeMine.length} aktif görüşmeniz var`}
          color="success"
          onClick={() => openChat(activeMine[0])}
          sx={{ position: 'fixed', bottom: 32, right: 96, zIndex: 1300 }}
        />
      )}

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(null)}
          severity={snackbar?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LiveSupportPanel;
